import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { formatTimestampInTimezone, type grace } from "@alm/domain";
import { PageHeader } from "@/components/app/shell/page-header";
import { AiCard } from "@/components/app/patterns/ai-card";
import { FactStrip } from "@/components/app/patterns/fact-strip";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft } from "@/lib/icons";
import { orpc } from "@/lib/orpc";
import { guardRoute } from "@/lib/policy/route-guard";
import { useZone } from "@/lib/facility/zone";
import {
  BAND_LABELS,
  durationLabel,
  MODE_LABELS,
  outcomeOf,
  recognitionLabel,
} from "../-components/grace-labels";
import { SessionTimeline } from "../-components/session-timeline";
import { SessionTranscript } from "../-components/session-transcript";

export const Route = createFileRoute("/_authenticated/_visitors/visitors/grace/$sessionId")({
  beforeLoad: ({ context }) => guardRoute(context.me, "visitors"),
  component: GraceSessionPage,
});

const SUMMARY_PENDING: Record<grace.WrapUpStatus, string> = {
  pending:
    "Grace writes a summary shortly after the conversation ends. The transcript has every word.",
  failed: "The summary could not be written. The transcript has every word.",
  skipped: "Nothing happened that needed a summary.",
  done: "No summary was written.",
};

/** One conversation at the kiosk: how it ended, who it was, what was said and what happened. */
function GraceSessionPage() {
  const { sessionId } = Route.useParams();
  const session = useQuery(
    orpc.visitors.grace.sessions.get.queryOptions({ input: { graceSessionId: sessionId } }),
  );
  const zone = useZone(session.data?.facilityId ?? null);

  const back = (
    <Button asChild variant="ghost" size="sm" className="self-start">
      <Link to="/visitors/grace">
        <ArrowLeft data-icon="inline-start" aria-hidden="true" />
        Grace
      </Link>
    </Button>
  );

  if (session.isError) {
    return (
      <div className="flex flex-col gap-5">
        {back}
        <Card className="gap-0 py-0">
          <ReadFailed
            className="p-4"
            message="Could not load this conversation."
            error={session.error}
            onRetry={() => void session.refetch()}
            retrying={session.isFetching}
          />
        </Card>
      </div>
    );
  }
  const row = session.data;
  if (!row) {
    return (
      <div className="flex flex-col gap-5" aria-busy="true">
        {back}
        <Skeleton className="h-9 w-72" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const outcome = outcomeOf(row.outcome);
  const where = [row.kioskName, row.kioskLocation].filter(Boolean).join(", ");
  const score = row.match?.score ?? null;
  const matchNote = !row.match
    ? "Security staff only"
    : score !== null
      ? `Score ${score.toFixed(2)}`
      : undefined;
  return (
    <div className="flex flex-col gap-5">
      {back}
      <PageHeader
        title={row.personName ? `Conversation with ${row.personName}` : "Conversation at the kiosk"}
        meta={[formatTimestampInTimezone(row.startedAt, zone), where].filter(Boolean).join(", ")}
      />
      <FactStrip
        label="About this conversation"
        facts={[
          { label: "Outcome", value: <Badge variant={outcome.tone}>{outcome.label}</Badge> },
          { label: "Recognition", value: recognitionLabel(row.recognition) },
          {
            label: "Match",
            value: row.match?.band ? BAND_LABELS[row.match.band] : "-",
            sub: matchNote,
          },
          {
            label: "Duration",
            value: <span className="tabular-nums">{durationLabel(row.durationSeconds)}</span>,
          },
          {
            label: "Mode",
            value: MODE_LABELS[row.mode],
            sub: `${row.toolCalls} tool ${row.toolCalls === 1 ? "call" : "calls"}${row.toolErrors > 0 ? `, ${row.toolErrors} failed` : ""}`,
          },
        ]}
      />
      <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="flex min-w-0 flex-col gap-5">
          <AiCard label="Summary by Grace">
            <CardContent>
              <p className="text-sm leading-relaxed text-pretty">
                {row.summary ?? SUMMARY_PENDING[row.wrapUpStatus]}
              </p>
            </CardContent>
          </AiCard>
          <SessionTranscript
            transcript={row.transcript}
            purgedAt={row.transcriptPurgedAt}
            startedAt={row.startedAt}
            zone={zone}
          />
        </div>
        <div className="flex min-w-0 flex-col gap-5">
          <Card>
            <CardContent className="flex flex-col gap-3 text-sm">
              <div>
                <p className="text-faint text-xs font-medium">Visitor</p>
                {row.personId && row.personName ? (
                  <Link
                    to="/visitors/people/$personId"
                    params={{ personId: row.personId }}
                    className="focus-ring rounded-sm font-medium hover:underline"
                  >
                    {row.personName}
                  </Link>
                ) : (
                  <p className="text-muted-foreground">Not identified</p>
                )}
              </div>
              <div>
                <p className="text-faint text-xs font-medium">Visit</p>
                {row.visit ? (
                  <p>
                    <span className="font-medium">
                      {row.visit.residentName || row.visit.visitingLabel || "The building"}
                    </span>
                    <span className="text-muted-foreground block text-xs tabular-nums">
                      In {formatTimestampInTimezone(row.visit.checkedInAt, zone)}
                      {row.visit.checkedOutAt
                        ? `, out ${formatTimestampInTimezone(row.visit.checkedOutAt, zone)}`
                        : ", still in the building"}
                    </span>
                  </p>
                ) : (
                  <p className="text-muted-foreground">No visit came of it</p>
                )}
              </div>
            </CardContent>
          </Card>
          <SessionTimeline events={row.events} zone={zone} />
        </div>
      </div>
    </div>
  );
}
